/**
 * The wallet had no front door.
 *
 * The backend has had the whole thing for a while: create a Stripe Checkout
 * session, poll it, credit once, keep a ledger. Nothing in the published
 * frontend called any of it, and /dashboard/wallet was not a route, so the
 * only way to add money was to call the API by hand.
 *
 * That also broke the return trip. The backend sends Stripe to
 * /dashboard/wallet?success=true&session_id=..., and the page that was
 * supposed to read that and ask the backend to credit the top-up did not
 * exist. A customer could pay and never be credited.
 */
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { readApiError } from '../lib/apiError';
import { Wallet, Loader2, CheckCircle, AlertCircle, Plus } from 'lucide-react';
import { formatMoney as dinero } from '@/lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTOS = [25, 50, 100, 250];
const MINIMO = 5;
const MAXIMO = 10000;


const WalletPage = () => {
  const { getAuthHeaders } = useAuth();
  const [params, setParams] = useSearchParams();

  const [balance, setBalance] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [monto, setMonto] = useState('50');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);

  const leerSaldo = useCallback(async () => {
    const res = await fetch(`${API}/wallet/balance`, { headers: getAuthHeaders() });
    const body = await res.json().catch(() => null);
    if (!res.ok) {
      setError(readApiError(res.status, body, 'Could not read the balance.'));
      return null;
    }
    setBalance(body.balance);
    return body.balance;
  }, [getAuthHeaders]);

  // Primera carga.
  useEffect(() => {
    leerSaldo().finally(() => setCargando(false));
  }, [leerSaldo]);

  /**
   * La vuelta desde Stripe.
   *
   * Stripe manda aca con ?success=true&session_id=... El webhook tambien
   * acredita, y los dos caminos pasan por la misma compuerta condicional en
   * el backend, asi que el que llegue segundo no suma de nuevo. Se consulta
   * igual porque el webhook puede no estar configurado, y entonces este es el
   * unico que acredita.
   */
  useEffect(() => {
    const sid = params.get('session_id');
    if (params.get('cancelled')) {
      setAviso({ tipo: 'aviso', texto: 'The payment was cancelled. Nothing was charged.' });
      setParams({}, { replace: true });
      return;
    }
    if (!sid || !params.get('success')) return;

    let vivo = true;
    (async () => {
      setAviso({ tipo: 'espera', texto: 'Confirming your payment with Stripe...' });
      // Stripe puede tardar un instante en marcar la sesion como pagada.
      for (let intento = 0; intento < 6 && vivo; intento++) {
        const res = await fetch(`${API}/wallet/topup/status/${sid}`, {
          headers: getAuthHeaders(),
        });
        const body = await res.json().catch(() => null);
        if (!res.ok) {
          setAviso(null);
          setError(readApiError(res.status, body, 'Could not confirm the payment.'));
          return;
        }
        if (body.payment_status === 'paid') {
          const nuevo = await leerSaldo();
          if (!vivo) return;
          setAviso({
            tipo: 'ok',
            texto: `${dinero(body.amount)} added. ${
              nuevo != null ? `Your balance is ${dinero(nuevo)}.` : ''
            }`,
          });
          setParams({}, { replace: true });
          return;
        }
        if (body.processing) {
          // ACH: Stripe ya tiene el pago, el banco lo confirma en unos dias.
          // No se acredita hasta entonces, y se dice en vez de esperar aca.
          setAviso({
            tipo: 'espera',
            texto:
              'Your bank payment is being processed. Bank transfers take 3 to 5 ' +
              'business days, and the balance updates by itself when the bank confirms.',
          });
          setParams({}, { replace: true });
          return;
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!vivo) return;
      // Fallar a la vista: el cobro puede haber salido y no podemos afirmarlo.
      setAviso(null);
      setError(
        'Stripe has not confirmed this payment yet. If you were charged, the ' +
          'balance will update on its own. Reload this page in a minute, and ' +
          'if it is still missing, keep the payment reference and contact support.'
      );
    })();

    return () => {
      vivo = false;
    };
  }, [params, setParams, getAuthHeaders, leerSaldo]);

  const recargar = async (e) => {
    e.preventDefault();
    setError(null);
    setAviso(null);

    const valor = Number(monto);
    if (!Number.isFinite(valor) || valor < MINIMO) {
      setError(`The minimum top-up is ${dinero(MINIMO)}.`);
      return;
    }
    if (valor > MAXIMO) {
      setError(`The maximum top-up is ${dinero(MAXIMO)}.`);
      return;
    }

    setEnviando(true);
    try {
      const res = await fetch(`${API}/wallet/topup`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: valor, origin_url: window.location.origin }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        setError(readApiError(res.status, body, 'The top-up could not be started.'));
        return;
      }
      const url = body && (body.url || body.checkout_url);
      if (!url) {
        // Nunca inventar un exito: sin url no hay pago que hacer.
        setError('Stripe did not return a payment page, so nothing was charged.');
        return;
      }
      window.location.href = url;
    } catch (err) {
      setError('Could not reach the server. Nothing was charged.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="wallet-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Wallet</h1>
        <p className="text-slate-500 mt-1">
          Your prepaid balance. Labels and services are charged against it.
        </p>
      </div>

      {aviso && (
        <div
          className={`rounded-lg border p-4 flex items-start gap-3 ${
            aviso.tipo === 'ok'
              ? 'border-emerald-200 bg-emerald-50'
              : 'border-slate-200 bg-slate-50'
          }`}
          data-testid="wallet-notice"
        >
          {aviso.tipo === 'ok' ? (
            <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
          ) : (
            <Loader2 className="w-5 h-5 text-slate-500 mt-0.5 shrink-0 animate-spin" />
          )}
          <p className="text-sm text-slate-700">{aviso.texto}</p>
        </div>
      )}

      {error && (
        <div
          className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-start gap-3"
          data-testid="wallet-error"
        >
          <AlertCircle className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
          <p className="text-sm text-rose-700">{error}</p>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <Wallet className="w-4 h-4" />
              Available balance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {cargando ? (
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            ) : (
              <p className="text-3xl font-semibold tracking-tight" data-testid="wallet-balance">
                {balance == null ? '—' : dinero(balance)}
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Add funds
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={recargar} className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {MONTOS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMonto(String(m))}
                    className={`px-4 py-2 rounded-lg border text-sm transition ${
                      monto === String(m)
                        ? 'border-slate-900 bg-slate-900 text-white'
                        : 'border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    {dinero(m)}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <span className="text-slate-500">$</span>
                <input
                  type="number"
                  min={MINIMO}
                  max={MAXIMO}
                  step="0.01"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                  className="w-40 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  data-testid="wallet-amount"
                  aria-label="Amount to add"
                />
                <button
                  type="submit"
                  disabled={enviando}
                  className="rounded-lg bg-slate-900 text-white px-5 py-2 text-sm disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="wallet-topup"
                >
                  {enviando && <Loader2 className="w-4 h-4 animate-spin" />}
                  Continue to payment
                </button>
              </div>

              <p className="text-xs text-slate-500">
                Between {dinero(MINIMO)} and {dinero(MAXIMO)}. Payment is handled by
                Stripe; card details never reach this site.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WalletPage;
